import type { Breadcrumb, Event } from "@sentry/browser";

// A document's link is signed for 31 days and works for anyone who has it, and the
// object's name holds the uploader's file name. Filters and search text live in the
// page's query string, and Auth0's code passes through it at login. None of that
// belongs in Sentry, so an address keeps its page and loses the rest.
const STORAGE_URL =
  /^https?:\/\/(?:firebasestorage|storage)\.googleapis\.com\//i;
const URL_IN_TEXT = /\bhttps?:\/\/[^\s"'`<>]+/gi;

const scrubUrl = (url: string) => {
  const storage = STORAGE_URL.exec(url);
  return storage ? `${storage[0]}[file]` : (url.split(/[?#]/, 1)[0] ?? "");
};

export const scrubText = (text: string) => text.replace(URL_IN_TEXT, scrubUrl);

// Sentry names clicked elements by these, and they can hold a candidate's name or
// the name of a document.
const NAMING_ATTRIBUTE = /\[(?:alt|aria-label|title)="[^"]*"\]/g;

// `arguments` holds what the console or an event handler got, click targets included,
// and `__serialized__` a plain object thrown as an error, whole. Either can hold
// anything, and an error's text still lists the object's keys.
const scrubData = (data: Record<string, unknown>) => {
  const { arguments: _arguments, __serialized__: _serialized, ...rest } = data;
  return Object.fromEntries(
    Object.entries(rest).map(([key, value]) => {
      if (typeof value !== "string") {
        return [key, value];
      }
      // Navigation's are relative, so they don't look like URLs in a text.
      return [
        key,
        key === "from" || key === "to" || key === "url"
          ? scrubUrl(value)
          : scrubText(value),
      ];
    }),
  );
};

export const scrubBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb => {
  const scrubbed = { ...breadcrumb };

  if (scrubbed.message) {
    scrubbed.message = scrubText(scrubbed.message);
    if (scrubbed.category?.startsWith("ui.")) {
      scrubbed.message = scrubbed.message.replace(NAMING_ATTRIBUTE, "");
    }
  }

  if (scrubbed.data) {
    scrubbed.data = scrubData(scrubbed.data);
  }

  return scrubbed;
};

export const scrubEvent = <T extends Event>(event: T): T => {
  const { request, message, exception, extra } = event;

  if (request?.url) {
    request.url = scrubUrl(request.url);
  }
  if (request?.headers?.Referer) {
    request.headers.Referer = scrubUrl(request.headers.Referer);
  }
  if (message) {
    event.message = scrubText(message);
  }
  if (extra) {
    event.extra = scrubData(extra);
  }
  for (const value of exception?.values ?? []) {
    if (value.value) {
      value.value = scrubText(value.value);
    }
    // An error from an inline or extension script is filed under the page's address.
    for (const frame of value.stacktrace?.frames ?? []) {
      if (frame.filename) {
        frame.filename = scrubUrl(frame.filename);
      }
    }
  }

  return event;
};
